// Klaviyo public IDs (browser-safe). Subscribing to UuL29L flips marketing
// consent to SUBSCRIBED, which fires the "Subscribed to Email Marketing"
// event that triggers the Welcome Series flow.
const KLAVIYO_COMPANY_ID = 'VpFPX6';
const KLAVIYO_EMAIL_LIST_ID = 'UuL29L';

class AccountDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel = this.querySelector('.zero-account-drawer__panel');
    this.tabsRoot = this.querySelector('.zero-account-drawer__tabs');
    this.tabButtons = this.querySelectorAll('[data-account-drawer-tab]');
    this.tabPanels = this.querySelectorAll('[data-account-drawer-panel]');
    this.closeEls = this.querySelectorAll('[data-account-drawer-close]');
    this.subscribeForm = this.querySelector('[data-account-drawer-subscribe]');

    this.lastTrigger = null;

    this.closeEls.forEach((el) => el.addEventListener('click', () => this.close()));
    this.tabButtons.forEach((btn) =>
      btn.addEventListener('click', () => this.switchTab(btn.dataset.accountDrawerTab))
    );
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    if (this.subscribeForm) {
      this.subscribeForm.addEventListener('submit', (e) => this.handleSubscribe(e));
    }

    this.wireHeaderTrigger();

    const initialTab = this.dataset.initialTab || 'register';
    this.switchTab(initialTab, { silent: true });

    if (this.dataset.initialOpen === 'true') {
      requestAnimationFrame(() => this.open());
    }
  }

  async handleSubscribe(e) {
    e.preventDefault();
    const form = this.subscribeForm;
    const submitBtn = form.querySelector('[data-subscribe-submit]');
    const errorEl = form.querySelector('[data-subscribe-error]');
    const emailInput = form.querySelector('input[name="email"]');
    const firstNameInput = form.querySelector('input[name="first_name"]');

    const email = (emailInput.value || '').trim();
    const firstName = (firstNameInput.value || '').trim();

    errorEl.hidden = true;
    errorEl.textContent = '';

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorEl.textContent = 'Please enter a valid email address.';
      errorEl.hidden = false;
      emailInput.focus();
      return;
    }

    const originalLabel = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Subscribing…';

    // Note: do NOT nest a `subscriptions` field inside profile.attributes —
    // the Client API rejects it ("'subscriptions' is not a valid field for
    // the resource 'profile'"). Posting to /client/subscriptions/ with a
    // list relationship is what flips consent to SUBSCRIBED.
    const profileAttrs = {
      email,
      properties: { 'Signup Source': 'Account Drawer' },
    };
    if (firstName) profileAttrs.first_name = firstName;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'ZERO Account Drawer',
          profile: { data: { type: 'profile', attributes: profileAttrs } },
        },
        relationships: { list: { data: { type: 'list', id: KLAVIYO_EMAIL_LIST_ID } } },
      },
    };

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-10-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (res.status === 202 || res.ok) {
        this.showSubscribeSuccess();
      } else {
        errorEl.textContent = "We couldn't subscribe you. Please try again or email info@zerodropshoe.com.";
        errorEl.hidden = false;
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
    } catch (err) {
      errorEl.textContent = "Network error. Please check your connection and try again.";
      errorEl.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;
    }
  }

  showSubscribeSuccess() {
    const formState = this.subscribeForm.querySelector('[data-subscribe-state="form"]');
    const successState = this.subscribeForm.querySelector('[data-subscribe-state="success"]');
    if (formState) formState.hidden = true;
    if (successState) successState.hidden = false;
  }

  wireHeaderTrigger() {
    const trigger = document.querySelector('.header__icon--account');
    if (!trigger) return;
    const isLoggedIn = document.documentElement.hasAttribute('data-customer-logged-in');
    if (isLoggedIn) return;
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(trigger);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.lastTrigger = triggeredBy;
    this.setAttribute('data-state', 'open');
    document.body.classList.add('zero-account-drawer-open');
    requestAnimationFrame(() => {
      const firstField = this.querySelector(
        '[data-account-drawer-panel]:not([hidden]) input:not([type="hidden"])'
      );
      if (firstField) firstField.focus();
    });
  }

  close() {
    this.removeAttribute('data-state');
    document.body.classList.remove('zero-account-drawer-open');
    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus();
    }
  }

  switchTab(name, { silent = false } = {}) {
    if (!name) return;
    this.tabsRoot.setAttribute('data-active', name);
    this.tabButtons.forEach((btn) => {
      const active = btn.dataset.accountDrawerTab === name;
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.tabPanels.forEach((panel) => {
      const active = panel.dataset.accountDrawerPanel === name;
      panel.hidden = !active;
    });
    if (!silent) {
      const firstField = this.querySelector(
        `[data-account-drawer-panel="${name}"] input:not([type="hidden"])`
      );
      if (firstField) firstField.focus();
    }
  }
}

if (!customElements.get('account-drawer')) {
  customElements.define('account-drawer', AccountDrawer);
}
